import React from "react";

import useRemoveAttachment from "@fartherfinance/frontend/api/Tasks/hooks/useRemoveAttachment";
import useRemoveTaskComment from "@fartherfinance/frontend/api/Tasks/hooks/useRemoveTaskComment";
import useUpdateTaskComment from "@fartherfinance/frontend/api/Tasks/hooks/useUpdateTaskComment";
import {
  TaskComment,
  TaskDetailsV2,
  TaskDetailsV3,
} from "@fartherfinance/frontend/api/Tasks/Types";
import {
  AttachmentId,
  TaskCommentId,
} from "@fartherfinance/frontend/api/Types";

import TaskCommentComponent from "../TaskComment/TaskComment";
import TaskSystemComment from "../TaskComment/TaskSystemComment";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Text from "@src/sharedComponents/Typography/Text";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import styles from "./TaskActivity.module.css";

interface TaskActivityProps {
  taskDetails: TaskDetailsV2 | TaskDetailsV3;
  taskComments: TaskComment[];
  isEmbedded?: boolean;
}

const TaskActivity: React.FC<TaskActivityProps> = ({
  taskDetails,
  taskComments,
  isEmbedded,
}) => {
  const auth = useAdvisorRequestAuth();
  const statusNotification = useStatusNotification();
  const callRemoveTaskComment = useRemoveTaskComment(auth);
  const callUpdateTaskComment = useUpdateTaskComment(auth);
  const callRemoveAttachment = useRemoveAttachment(auth);

  const handleRemoveTaskComment = async (
    commentId: TaskCommentId
  ): Promise<void> => {
    try {
      await callRemoveTaskComment({ taskId: taskDetails.id, commentId });

      trackEvent({ name: "Advisor Tasks Comment Delete" });
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to delete comment."),
        "Error"
      );
    }
  };

  const handleUpdateTaskComment = async (
    commentId: TaskCommentId,
    content: string
  ): Promise<void> => {
    try {
      await callUpdateTaskComment({
        taskId: taskDetails.id,
        commentId,
        request: { content },
      });

      trackEvent({ name: "Advisor Tasks Comment Update" });
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to update comment."),
        "Error"
      );
    }
  };

  const handleDeleteAttachment = async (
    attachmentId: AttachmentId
  ): Promise<void> => {
    try {
      await callRemoveAttachment({ taskId: taskDetails.id, attachmentId });

      trackEvent({ name: "Advisor Tasks Attachment Delete" });

      statusNotification("Attachment removed successfully.", "Success");
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to delete attachment."),
        "Error"
      );
    }
  };

  return (
    <div
      className={toClassName(styles.container, {
        [styles.embedded]: isEmbedded,
      })}
    >
      <Text variant="title3">Activity</Text>

      <div className={styles.commentsContainer}>
        <TaskSystemComment
          comment="created the task"
          manipulator={taskDetails.creator}
          timestamp={taskDetails.createdTs}
        />

        {taskDetails.modifiedBy && (
          <TaskSystemComment
            comment="updated the task"
            manipulator={taskDetails.modifiedBy}
            timestamp={taskDetails.updatedTs}
          />
        )}

        {taskComments.map((comment) => (
          <TaskCommentComponent
            key={comment.id}
            comment={comment}
            onCommentDelete={handleRemoveTaskComment}
            onCommentUpdate={handleUpdateTaskComment}
            onAttachmentDelete={handleDeleteAttachment}
          />
        ))}
      </div>
    </div>
  );
};

export default TaskActivity;
